import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Mail, Phone, MessageSquare, Send } from "lucide-react";
import { LOGO_URL } from "@/config/branding";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F5F5F7' }} data-testid="contact-page">
      {/* Nav */}
      <nav className="bg-white/95 backdrop-blur-md border-b border-[#E5E5EA] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <Link to="/">
            <img src={LOGO_URL} alt="Self Certify UK" className="h-12 sm:h-14 object-contain object-left" />
          </Link>
          <div className="flex items-center gap-3 sm:gap-4">
            <Link to="/" className="text-sm font-medium hover:text-[#00A3E0] transition-colors" style={{ fontFamily: 'Outfit, sans-serif', color: '#8E8E93' }}>
              Home
            </Link>
            <Link to="/login" className="bg-[#00A3E0] text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-[#0082B3] transition-all" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Sign In
            </Link>
          </div>
        </div>
      </nav>

      {/* Contact Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-lg">
          {sent ? (
            <div className="bg-white rounded-2xl p-10 text-center animate-fade-in-up" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }} data-testid="contact-success">
              <div className="w-16 h-16 bg-[#EAFBF0] flex items-center justify-center rounded-full mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-[#22C55E]" />
              </div>
              <h2 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif', color: '#1C1C1E' }}>Message Sent</h2>
              <p className="text-sm mb-8" style={{ color: '#8E8E93' }}>
                Thank you for contacting us. We'll get back to you as soon as possible.
              </p>
              <Link to="/" className="bg-[#D4A537] text-white font-semibold px-6 py-2.5 rounded-xl inline-block hover:bg-[#B8892C] transition-all" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Back to Home
              </Link>
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-8 sm:p-10 animate-fade-in-up" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: '#1C1C1E' }}>
                Get in Touch
              </h2>
              <p className="text-sm mb-8" style={{ color: '#8E8E93' }}>
                Have a question or need support? Fill in the form below.
              </p>

              {error && (
                <div className="rounded-xl bg-red-50 p-3 mb-6 text-sm text-red-600 border border-red-200" data-testid="contact-error">
                  {error}
                </div>
              )}

              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div>
                  <label className="text-xs font-semibold mb-1.5 block" style={{ fontFamily: 'Outfit, sans-serif', color: '#8E8E93' }}>Name</label>
                  <input data-testid="contact-name" className="brutalist-input" value={form.name} onChange={update("name")} required placeholder="Your full name" />
                </div>
                <div>
                  <label className="text-xs font-semibold mb-1.5 block" style={{ fontFamily: 'Outfit, sans-serif', color: '#8E8E93' }}>Email</label>
                  <input data-testid="contact-email" type="email" className="brutalist-input" value={form.email} onChange={update("email")} required placeholder="your@email.co.uk" />
                </div>
                <div>
                  <label className="text-xs font-semibold mb-1.5 block" style={{ fontFamily: 'Outfit, sans-serif', color: '#8E8E93' }}>Phone (optional)</label>
                  <input data-testid="contact-phone" type="tel" className="brutalist-input" value={form.phone} onChange={update("phone")} placeholder="07xxx xxxxxx" />
                </div>
                <div>
                  <label className="text-xs font-semibold mb-1.5 block" style={{ fontFamily: 'Outfit, sans-serif', color: '#8E8E93' }}>Message</label>
                  <textarea data-testid="contact-message" className="brutalist-input" rows={5} value={form.message} onChange={update("message")} required placeholder="How can we help?" style={{ minHeight: '120px' }} />
                </div>
                <button type="submit" disabled={loading} className="brutalist-btn w-full flex items-center justify-center gap-2" style={{ backgroundColor: '#00A3E0' }} data-testid="contact-submit">
                  {loading ? "Sending..." : <><Send className="w-4 h-4" /> Send Message</>}
                </button>
              </form>

              <div className="mt-8 pt-6 border-t border-[#E5E5EA] flex flex-col sm:flex-row gap-4 text-xs" style={{ color: '#8E8E93' }}>
                <a href="mailto:selfcertifyuk@gmail.com" className="flex items-center gap-2 hover:text-[#00A3E0] transition-colors">
                  <Mail className="w-3.5 h-3.5" /> selfcertifyuk@gmail.com
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#1C1C1E] py-8 mt-auto">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <img src={LOGO_URL} alt="Self Certify UK" className="h-8 object-contain" />
            <div className="flex items-center gap-6 text-sm text-white/40">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/delete-account" className="hover:text-white transition-colors">Delete Account</Link>
            </div>
          </div>
          <div className="mt-6 pt-4 border-t border-white/10 text-center text-xs text-white/30">
            &copy; {new Date().getFullYear()} Self Certify UK. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
